/**
 * `smplify-default` — captures the console's look as of the day the theme SDK
 * shipped. Values are lifted verbatim from `src/index.css` and from the
 * `brand` colors defined in `tailwind.config.ts` (converted from hex to HSL).
 *
 * This preset has two purposes:
 *   1. Be the fallback every time a tenant theme is cleared or invalid.
 *   2. Serve as a faithful "before" reference for visual regression tests
 *      after we start mutating CSS variables at runtime.
 *
 * If `index.css` defaults change in future, update this file in the same PR
 * so the round trip (apply default → reset → re-apply) stays a no-op.
 */
import type { ThemePack } from "@/theme/types";

export const smplifyDefault: ThemePack = {
  meta: {
    slug: "smplify-default",
    name: "Smplify",
    description: "The default Smplify console look — neutral and product-first.",
    author: "Smplify",
    version: "1.0.0",
    supportedModes: "both",
  },
  light: {
    colors: {
      background: "0 0% 100%",
      foreground: "222.2 84% 4.9%",
      card: "0 0% 100%",
      cardForeground: "222.2 84% 4.9%",
      popover: "0 0% 100%",
      popoverForeground: "222.2 84% 4.9%",
      primary: "222.2 47.4% 11.2%",
      primaryForeground: "210 40% 98%",
      secondary: "210 40% 96.1%",
      secondaryForeground: "222.2 47.4% 11.2%",
      muted: "210 40% 96.1%",
      mutedForeground: "215.4 16.3% 46.9%",
      accent: "210 40% 96.1%",
      accentForeground: "222.2 47.4% 11.2%",
      destructive: "0 84.2% 60.2%",
      destructiveForeground: "210 40% 98%",
      success: "152 69% 41%",
      successForeground: "0 0% 100%",
      successMuted: "152 69% 95%",
      successMutedForeground: "152 69% 30%",
      successBorder: "152 69% 80%",
      warning: "38 92% 50%",
      warningForeground: "38 92% 20%",
      warningMuted: "38 92% 95%",
      warningMutedForeground: "38 92% 30%",
      warningBorder: "38 92% 75%",
      info: "217 91% 60%",
      infoForeground: "0 0% 100%",
      infoMuted: "217 91% 95%",
      infoMutedForeground: "217 91% 35%",
      infoBorder: "217 91% 80%",
      border: "214.3 31.8% 91.4%",
      input: "214.3 31.8% 91.4%",
      ring: "222.2 84% 4.9%",
      sidebarBackground: "0 0% 98%",
      sidebarForeground: "240 5.3% 26.1%",
      sidebarPrimary: "240 5.9% 10%",
      sidebarPrimaryForeground: "0 0% 98%",
      sidebarAccent: "240 4.8% 95.9%",
      sidebarAccentForeground: "240 5.9% 10%",
      sidebarBorder: "220 13% 91%",
      sidebarRing: "217.2 91.2% 59.8%",
      // Brand tokens — converted from #ffffff / #333333 / #666666 / #000000.
      brandBackground: "0 0% 100%",
      brandMain: "0 0% 20%",
      brandAuxiliary1: "0 0% 40%",
      brandAuxiliary2: "0 0% 0%",
    },
    charts: {
      chart1: "220 70% 50%",
      chart2: "160 60% 45%",
      chart3: "30 80% 55%",
      chart4: "280 65% 60%",
      chart5: "120 60% 50%",
      chart6: "0 70% 50%",
      chart7: "200 80% 50%",
      chart8: "45 90% 55%",
      chart9: "300 70% 55%",
      chart10: "180 50% 40%",
      chart11: "250 60% 55%",
      chart12: "15 85% 50%",
    },
  },
  dark: {
    colors: {
      background: "222.2 84% 4.9%",
      foreground: "210 40% 98%",
      card: "222.2 84% 4.9%",
      cardForeground: "210 40% 98%",
      popover: "222.2 84% 4.9%",
      popoverForeground: "210 40% 98%",
      primary: "210 40% 98%",
      primaryForeground: "222.2 47.4% 11.2%",
      secondary: "217.2 32.6% 17.5%",
      secondaryForeground: "210 40% 98%",
      muted: "217.2 32.6% 17.5%",
      mutedForeground: "215 20.2% 65.1%",
      accent: "217.2 32.6% 17.5%",
      accentForeground: "210 40% 98%",
      destructive: "0 62.8% 30.6%",
      destructiveForeground: "210 40% 98%",
      success: "152 69% 50%",
      successForeground: "0 0% 100%",
      successMuted: "152 50% 12%",
      successMutedForeground: "152 69% 65%",
      successBorder: "152 40% 25%",
      warning: "38 80% 50%",
      warningForeground: "38 92% 95%",
      warningMuted: "38 50% 12%",
      warningMutedForeground: "38 80% 65%",
      warningBorder: "38 40% 25%",
      info: "217 91% 60%",
      infoForeground: "0 0% 100%",
      infoMuted: "217 50% 12%",
      infoMutedForeground: "217 80% 70%",
      infoBorder: "217 40% 25%",
      border: "217.2 32.6% 17.5%",
      input: "217.2 32.6% 17.5%",
      ring: "212.7 26.8% 83.9%",
      sidebarBackground: "240 5.9% 10%",
      sidebarForeground: "240 4.8% 95.9%",
      sidebarPrimary: "224.3 76.3% 48%",
      sidebarPrimaryForeground: "0 0% 100%",
      sidebarAccent: "240 3.7% 15.9%",
      sidebarAccentForeground: "240 4.8% 95.9%",
      sidebarBorder: "240 3.7% 15.9%",
      sidebarRing: "217.2 91.2% 59.8%",
      // Brand tokens flip on dark so `bg-brand-background` doesn't blow out.
      brandBackground: "222.2 84% 4.9%",
      brandMain: "210 40% 90%",
      brandAuxiliary1: "215 20.2% 65.1%",
      brandAuxiliary2: "210 40% 98%",
    },
    charts: {
      chart1: "220 70% 60%",
      chart2: "160 55% 55%",
      chart3: "30 75% 65%",
      chart4: "280 60% 70%",
      chart5: "120 55% 60%",
      chart6: "0 65% 60%",
      chart7: "200 75% 60%",
      chart8: "45 85% 65%",
      chart9: "300 65% 65%",
      chart10: "180 45% 50%",
      chart11: "250 55% 65%",
      chart12: "15 80% 60%",
    },
  },
  typography: {
    displayFont: "'Poppins', sans-serif",
    bodyFont: "'Poppins', sans-serif",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
  },
  surfaces: {
    radius: "0.5rem",
  },
};

export const DEFAULT_THEME = smplifyDefault;
